/**
 * Employee profile PDF export — renderer (HOW the PDF is drawn).
 *
 * Consumes the declarative blocks emitted by `employeePdfSchema.js`, so this
 * file only knows about drawing primitives (hero card, section bars, key/value
 * grids, sub-records, document-image grids) — never about specific fields.
 * (The directory export is an Excel workbook — see `employeeExcel.js`.)
 *
 * Public API:
 *   exportEmployeePdf({ email, options, onError, onSuccess })
 *     Single employee -> PDF in a new tab.
 *
 *   options = {
 *     detail: 'detailed' | 'summary',
 *     sections: { [sectionKey]: boolean },
 *     includeImages: boolean,
 *     imageParts: { [partKey]: boolean }
 *   }
 */
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import dayjs from 'dayjs';

import companyLogo from '@assets/logo/echologyx-wordmark.svg';

import {
    fetchEmployee,
    fetchCalendarName,
    fetchSecureFileBlobUrl,
    secureFileViewerLink
} from './employeeExportApi';
import {
    EXPORT_SECTIONS,
    collectDocumentUrls,
    normalizeEmployee,
    fullNameOf,
    initialsOf
} from './employeePdfSchema';

/* ------------------------------------------------------------------ */
/* Design tokens                                                       */
/* ------------------------------------------------------------------ */
const BRAND = [250, 75, 0]; // #FA4B00
const DARK = [33, 33, 33]; // #212121
const MUTED = [110, 110, 118];
const LINE = [226, 226, 231];
const LABEL_BG = [247, 247, 249];
const HERO_BG = [250, 250, 251];
const WHITE = [255, 255, 255];

const PAGE = { w: 595.28, h: 841.89 }; // A4 portrait, points
const M = 40; // side margin
const CONTENT_W = PAGE.w - M * 2; // 515.28
const CONTENT_TOP = 64; // content resumes below the running header
const CONTENT_BOTTOM = 56; // footer reserve

/* ------------------------------------------------------------------ */
/* Small helpers                                                       */
/* ------------------------------------------------------------------ */
const valOr = (v, fallback = 'N/A') =>
    v === undefined || v === null || v === '' ? fallback : v;

const fmtDate = (v) => {
    if (!v) return 'N/A';
    const d = dayjs(v);
    return d.isValid() ? d.format('DD MMM YYYY') : 'N/A';
};

/**
 * Coerce a value into printable text ('N/A' for empties/objects,
 * guards against "Invalid Date").
 */
const printable = (v) => {
    if (v === undefined || v === null || v === '') return 'N/A';
    if (typeof v === 'object') {
        if ('link' in v) return v.link ? 'Provided' : 'N/A';
        return 'N/A';
    }
    const s = String(v).trim();
    if (!s || s.toLowerCase() === 'invalid date') return 'N/A';
    return s;
};

/* ------------------------------------------------------------------ */
/* Non-Latin glyphs (e.g. Bengali) — imaged via the browser's fonts    */
/* ------------------------------------------------------------------ */
const needsGlyphImage = (s) => {
    if (typeof s !== 'string') return false;
    for (let i = 0; i < s.length; i += 1) {
        if (s.charCodeAt(i) > 0xff) return true; // beyond Latin-1
    }
    return false;
};

const textToImage = (text, color) => {
    const scale = 4; // supersample for sharpness
    const fontPx = 16;
    const fontStack =
        '"Noto Sans Bengali","Nirmala UI","Vrinda","Bangla MN","Kohinoor Bangla","Shonar Bangla","Segoe UI",sans-serif';
    const meas = document.createElement('canvas').getContext('2d');
    meas.font = `${fontPx}px ${fontStack}`;
    const textW = Math.max(1, meas.measureText(text).width);
    const boxW = textW + 4;
    const boxH = Math.ceil(fontPx * 1.5);
    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(boxW * scale);
    canvas.height = Math.ceil(boxH * scale);
    const ctx = canvas.getContext('2d');
    ctx.scale(scale, scale);
    ctx.font = `${fontPx}px ${fontStack}`;
    ctx.textBaseline = 'middle';
    ctx.fillStyle = `rgb(${color[0]}, ${color[1]}, ${color[2]})`;
    ctx.fillText(text, 2, boxH / 2);
    return { dataUrl: canvas.toDataURL('image/png'), ar: boxW / boxH };
};

const drawCellImage = (doc, cell, img) => {
    const pad = 8;
    const availW = cell.width - pad * 2;
    const availH = cell.height - 6;
    let h = 11; // target text height (pt)
    let w = h * img.ar;
    if (w > availW) {
        w = availW;
        h = w / img.ar;
    }
    if (h > availH) {
        h = availH;
        w = h * img.ar;
    }
    try {
        doc.addImage(
            img.dataUrl,
            'PNG',
            cell.x + pad,
            cell.y + (cell.height - h) / 2,
            w,
            h
        );
    } catch (e) {
        /* ignore */
    }
};

/* ------------------------------------------------------------------ */
/* Image loading (best-effort, never throws)                           */
/* ------------------------------------------------------------------ */
const loadImageDataUrl = (url, { scale = 1 } = {}) =>
    new Promise((resolve) => {
        if (!url || typeof url !== 'string') return resolve(null);
        const img = new Image();
        img.crossOrigin = 'anonymous';
        let settled = false;
        const done = (v) => {
            if (!settled) {
                settled = true;
                resolve(v);
            }
        };
        const timer = setTimeout(() => done(null), 8000);
        img.onload = () => {
            clearTimeout(timer);
            try {
                const w = img.naturalWidth || img.width;
                const h = img.naturalHeight || img.height;
                if (!w || !h) return done(null);
                const canvas = document.createElement('canvas');
                canvas.width = Math.ceil(w * scale);
                canvas.height = Math.ceil(h * scale);
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                done({
                    dataUrl: canvas.toDataURL('image/png'),
                    w,
                    h,
                    format: 'PNG'
                });
            } catch (e) {
                // Tainted canvas (missing CORS headers) etc. — skip gracefully.
                done(null);
            }
        };
        img.onerror = () => {
            clearTimeout(timer);
            done(null);
        };
        img.src = url;
    });

/**
 * Load a SECURED employee document (photograph, NID, …): fetch the bytes
 * through the API with the auth token (same mechanism as the leave-document
 * viewer), then rasterize via an object URL — which is same-origin, so the
 * canvas is never tainted.
 */
const loadDocumentImage = async (path) => {
    const blobUrl = await fetchSecureFileBlobUrl(path);
    if (!blobUrl) return null;
    const img = await loadImageDataUrl(blobUrl);
    window.URL.revokeObjectURL(blobUrl);
    return img;
};

/* ------------------------------------------------------------------ */
/* Drawing primitives                                                  */
/* ------------------------------------------------------------------ */
const ensureSpace = (doc, y, needed) => {
    if (y + needed > PAGE.h - CONTENT_BOTTOM) {
        doc.addPage();
        return CONTENT_TOP;
    }
    return y;
};

const drawChip = (doc, x, y, text, variant = 'default') => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    const padX = 9;
    const h = 17;
    const w = doc.getTextWidth(text) + padX * 2;

    let fill = WHITE;
    let border = LINE;
    let txt = DARK;
    if (variant === 'success') {
        fill = [236, 250, 241];
        border = [167, 224, 190];
        txt = [27, 128, 74];
    } else if (variant === 'danger') {
        fill = [253, 238, 238];
        border = [240, 190, 190];
        txt = [190, 44, 44];
    }

    doc.setFillColor(...fill);
    doc.setDrawColor(...border);
    doc.setLineWidth(0.8);
    doc.roundedRect(x, y, w, h, 8.5, 8.5, 'FD');
    doc.setTextColor(...txt);
    doc.text(text, x + padX, y + h / 2 + 2.8);
    return w;
};

const drawHero = (doc, e, photo, top, detailLabel) => {
    const x = M;
    const w = CONTENT_W;
    const h = 132;
    const y = top + 8;

    doc.setFillColor(...HERO_BG);
    doc.setDrawColor(...LINE);
    doc.setLineWidth(0.75);
    doc.roundedRect(x, y, w, h, 8, 8, 'FD');
    doc.setFillColor(...BRAND);
    doc.roundedRect(x, y, 5, h, 2, 2, 'F');

    // Photo / initials avatar
    const ps = 92;
    const px = x + 20;
    const py = y + (h - ps) / 2;
    const initialsAvatar = () => {
        doc.setFillColor(...BRAND);
        doc.roundedRect(px, py, ps, ps, 6, 6, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(30);
        doc.setTextColor(...WHITE);
        doc.text(initialsOf(e), px + ps / 2, py + ps / 2 + 10, {
            align: 'center'
        });
    };
    if (photo) {
        doc.setFillColor(...WHITE);
        doc.roundedRect(px, py, ps, ps, 6, 6, 'F');
        try {
            doc.addImage(photo.dataUrl, photo.format || 'PNG', px, py, ps, ps);
        } catch (err) {
            initialsAvatar();
        }
        doc.setDrawColor(...LINE);
        doc.setLineWidth(0.75);
        doc.roundedRect(px, py, ps, ps, 6, 6, 'S');
    } else {
        initialsAvatar();
    }

    // Text block
    const tx = px + ps + 20;
    let ty = y + 32;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(17);
    doc.setTextColor(...DARK);
    doc.text(fullNameOf(e), tx, ty);

    ty += 18;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10.5);
    doc.setTextColor(...BRAND);
    doc.text(valOr(e.designation?.name, '-'), tx, ty);

    ty += 15;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    doc.setTextColor(...MUTED);
    doc.text(
        [e.department?.name, e.branch?.name].filter(Boolean).join('   ·   ') ||
            '-',
        tx,
        ty
    );

    ty += 14;
    doc.setFontSize(9);
    doc.text(valOr(e.user?.email || e.contact?.officialEmail, '-'), tx, ty);

    // Chips
    const chips = [
        { text: `ID · ${valOr(e.employeeId, '-')}` },
        detailLabel ? { text: detailLabel } : null,
        { text: `Joined · ${fmtDate(e.joiningDate)}` },
        e.resignationDate
            ? { text: 'Resigned', variant: 'danger' }
            : { text: 'Active', variant: 'success' }
    ].filter(Boolean);

    let cx = tx;
    const cy = y + h - 30;
    chips.forEach((c) => {
        cx += drawChip(doc, cx, cy, c.text, c.variant) + 8;
    });

    return y + h + 18;
};

const drawSectionTitle = (doc, title, y) => {
    y = ensureSpace(doc, y, 56); // keep the title with at least one row
    const h = 22;
    doc.setFillColor(...BRAND);
    doc.roundedRect(M, y, CONTENT_W, h, 3, 3, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10.5);
    doc.setTextColor(...WHITE);
    doc.text(title.toUpperCase(), M + 12, y + h / 2 + 3.5, { charSpace: 0.5 });
    return y + h + 8;
};

const drawBlockHeading = (doc, y, text) => {
    y = ensureSpace(doc, y, 44);
    doc.setFillColor(...BRAND);
    doc.rect(M, y - 7, 3, 11, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.5);
    doc.setTextColor(...DARK);
    doc.text(text, M + 9, y + 2);
    return y + 9;
};

const emptyNote = (doc, y, text) => {
    y = ensureSpace(doc, y, 24);
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(9);
    doc.setTextColor(...MUTED);
    doc.text(text, M + 2, y + 4);
    return y + 20;
};

const LABEL_CELL = { fillColor: LABEL_BG, fontStyle: 'bold', textColor: DARK };
const VALUE_CELL = { fillColor: WHITE, textColor: DARK };

/**
 * Render label/value pairs as a bordered grid.
 * perRow = pairs per row (2 => four visual columns; 1 => label|value row).
 */
const kvTable = (doc, y, pairsRaw, { perRow = 2 } = {}) => {
    const pairs = (pairsRaw || []).filter(Boolean);
    if (!pairs.length) return emptyNote(doc, y, 'No information provided.');

    const body = [];
    for (let i = 0; i < pairs.length; i += perRow) {
        const slice = pairs.slice(i, i + perRow);
        const row = [];
        slice.forEach((p, idx) => {
            row.push({ content: p.label, styles: { ...LABEL_CELL } });
            const isShortTail =
                slice.length < perRow && idx === slice.length - 1;
            const valueText = printable(p.value);
            const valueCell = {
                content: valueText,
                colSpan: isShortTail ? 2 * (perRow - slice.length) + 1 : 1,
                styles: { ...VALUE_CELL }
            };
            // Non-Latin scripts (e.g. Bengali) can't be drawn with the
            // built-in fonts — render them as an image instead.
            if (needsGlyphImage(valueText)) {
                valueCell.content = '';
                valueCell._img = textToImage(valueText, DARK);
            }
            row.push(valueCell);
        });
        body.push(row);
    }

    autoTable(doc, {
        startY: y,
        body,
        theme: 'grid',
        styles: {
            font: 'helvetica',
            fontSize: 9,
            cellPadding: { top: 5.5, bottom: 5.5, left: 8, right: 8 },
            lineColor: LINE,
            lineWidth: 0.5,
            textColor: DARK,
            valign: 'middle',
            overflow: 'linebreak'
        },
        columnStyles:
            perRow === 2
                ? {
                      0: { cellWidth: 90 },
                      1: { cellWidth: 167.64 },
                      2: { cellWidth: 90 },
                      3: { cellWidth: 167.64 }
                  }
                : { 0: { cellWidth: 150 }, 1: { cellWidth: 365.28 } },
        margin: { left: M, right: M, top: CONTENT_TOP, bottom: CONTENT_BOTTOM },
        tableWidth: CONTENT_W,
        didDrawCell: (data) => {
            const raw = data.cell.raw;
            if (raw && raw._img) drawCellImage(doc, data.cell, raw._img);
        }
    });

    return doc.lastAutoTable.finalY + 14;
};

/** Two-per-row grid of framed document images with captions. */
const drawImagesGrid = (doc, y, items, imageMap) => {
    const list = (items || []).filter((it) => it && it.url);
    if (!list.length) return y;

    const gap = 12;
    const cellW = (CONTENT_W - gap) / 2;
    const capH = 16;
    const imgAreaH = 150;
    const frameH = capH + imgAreaH;

    for (let i = 0; i < list.length; i += 2) {
        const row = list.slice(i, i + 2);
        y = ensureSpace(doc, y, frameH + 10);
        const rowY = y;
        row.forEach((it, idx) => {
            const x = M + idx * (cellW + gap);

            doc.setFont('helvetica', 'bold');
            doc.setFontSize(8.5);
            doc.setTextColor(...MUTED);
            doc.text(it.label.toUpperCase(), x + 2, rowY + 8, {
                charSpace: 0.5
            });

            // Permanent in-app link to the secured document (viewer page).
            const href = secureFileViewerLink(it.url);
            if (href) {
                const linkText = 'OPEN';
                const linkW = doc.getTextWidth(linkText);
                doc.setTextColor(...BRAND);
                doc.textWithLink(linkText, x + cellW - linkW - 2, rowY + 8, {
                    url: href
                });
            }

            const fy = rowY + capH;
            doc.setFillColor(...WHITE);
            doc.setDrawColor(...LINE);
            doc.setLineWidth(0.75);
            doc.roundedRect(x, fy, cellW, imgAreaH, 4, 4, 'FD');

            const img = imageMap[it.url];
            if (img) {
                const maxW = cellW - 16;
                const maxH = imgAreaH - 16;
                let w = maxW;
                let h = (img.h / img.w) * w;
                if (h > maxH) {
                    h = maxH;
                    w = (img.w / img.h) * h;
                }
                try {
                    doc.addImage(
                        img.dataUrl,
                        img.format || 'PNG',
                        x + (cellW - w) / 2,
                        fy + (imgAreaH - h) / 2,
                        w,
                        h
                    );
                } catch (e) {
                    /* keep the empty frame */
                }
            } else {
                doc.setFont('helvetica', 'italic');
                doc.setFontSize(9);
                doc.setTextColor(...MUTED);
                doc.text(
                    'Preview unavailable - use the OPEN link',
                    x + cellW / 2,
                    fy + imgAreaH / 2,
                    { align: 'center' }
                );
            }

            // The whole frame is clickable too.
            if (href) doc.link(x, fy, cellW, imgAreaH, { url: href });
        });
        y += frameH + 12;
    }
    return y + 2;
};

/** Walk the schema blocks and draw each one. */
const renderBlocks = (doc, y, blocks, imageMap) => {
    (blocks || []).forEach((block) => {
        if (!block) return;
        switch (block.kind) {
            case 'kv':
                y = kvTable(doc, y, block.pairs, { perRow: block.perRow || 2 });
                break;
            case 'subtitle':
                y = drawBlockHeading(doc, y, block.text);
                y += 4;
                break;
            case 'entities':
                (block.items || []).forEach((item) => {
                    y = drawBlockHeading(doc, y, item.label);
                    y = kvTable(doc, y, item.pairs, { perRow: 2 });
                    if (item.images && item.images.length) {
                        y = drawImagesGrid(doc, y, item.images, imageMap);
                    }
                });
                break;
            case 'images':
                y = drawImagesGrid(doc, y, block.items, imageMap);
                break;
            case 'note':
                y = emptyNote(doc, y, block.text);
                break;
            default:
                break;
        }
    });
    return y;
};

/**
 * Draw one full employee profile (hero + selected sections) starting on the
 * CURRENT page.
 */
const renderEmployeeProfile = (
    doc,
    e,
    { detailed, sections, parts, imageMap, calendarName }
) => {
    const photoUrl = e.identification?.photograph;
    const heroPhoto = parts.photograph && photoUrl ? imageMap[photoUrl] : null;

    let y = drawHero(
        doc,
        e,
        heroPhoto,
        CONTENT_TOP,
        detailed ? null : 'Summary Report'
    );

    const ctx = { e, detailed, parts, calendarName };
    EXPORT_SECTIONS.forEach((section) => {
        if (!sections[section.key]) return;
        y = drawSectionTitle(doc, section.label, y);
        const blocks = section.build(ctx);
        y = renderBlocks(doc, y, blocks, imageMap);
    });
};

const drawRunningHeader = (doc, logo) => {
    const topY = 22;
    if (logo) {
        const h = 14;
        const w = (logo.w / logo.h) * h;
        try {
            doc.addImage(logo.dataUrl, logo.format || 'PNG', M, topY, w, h);
        } catch (e) {
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(13);
            doc.setTextColor(...DARK);
            doc.text('ECHOLOGYX', M, topY + 12);
        }
    } else {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(13);
        doc.setTextColor(...DARK);
        doc.text('ECHOLOGYX', M, topY + 12);
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(...MUTED);
    doc.text('EMPLOYEE PROFILE', PAGE.w - M, topY + 11, {
        align: 'right',
        charSpace: 1
    });

    doc.setDrawColor(...BRAND);
    doc.setLineWidth(1.5);
    doc.line(M, 50, PAGE.w - M, 50);
};

const drawFooter = (doc, centerText, page, total) => {
    const yF = PAGE.h - 30;
    doc.setDrawColor(...LINE);
    doc.setLineWidth(0.5);
    doc.line(M, yF - 9, PAGE.w - M, yF - 9);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...MUTED);
    doc.text(
        `Generated ${dayjs().format('DD MMM YYYY, h:mm A')}  ·  Confidential`,
        M,
        yF
    );
    doc.text(centerText, PAGE.w / 2, yF, { align: 'center' });
    doc.text(`Page ${page} of ${total}`, PAGE.w - M, yF, { align: 'right' });
};

/** Stamp the running header + footer on every page (call once, at the end). */
const finalizeDoc = (doc, { logo, footerCenter }) => {
    const pages = doc.getNumberOfPages();
    for (let p = 1; p <= pages; p += 1) {
        doc.setPage(p);
        drawRunningHeader(doc, logo);
        drawFooter(doc, footerCenter, p, pages);
    }
};

/* ------------------------------------------------------------------ */
/* Loading tab                                                         */
/* ------------------------------------------------------------------ */
const loadingHtml = () => `<!doctype html><html><head><meta charset="utf-8">
<title>Generating PDF…</title><style>
  html,body{height:100%;margin:0}
  body{display:flex;align-items:center;justify-content:center;
    font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;
    background:#fafafb;color:#212121}
  .box{text-align:center}
  .spinner{width:44px;height:44px;margin:0 auto 18px;border:4px solid #f0d8cc;
    border-top-color:#FA4B00;border-radius:50%;animation:spin .8s linear infinite}
  p{margin:0;font-size:14px;color:#6e6e76}
  @keyframes spin{to{transform:rotate(360deg)}}
</style></head><body><div class="box">
  <div class="spinner"></div><p>Generating employee profile…</p>
</div></body></html>`;

const openLoadingTab = () => {
    const win = window.open('', '_blank');
    if (win) {
        try {
            win.document.write(loadingHtml());
            win.document.close();
        } catch (e) {
            /* cross-origin write guard — ignore */
        }
    }
    return win;
};

const closeLoadingTab = (win) => {
    if (!win) return;
    try {
        win.close();
    } catch (e) {
        /* ignore */
    }
};

/* ------------------------------------------------------------------ */
/* Public orchestrator                                                 */
/* ------------------------------------------------------------------ */
export async function exportEmployeePdf({
    email,
    options = {},
    onError,
    onSuccess
} = {}) {
    if (!email) {
        onError && onError('No employee email found for this row.');
        return;
    }

    const detailed = options.detail !== 'summary';
    const sections = options.sections || {};
    const parts = options.includeImages ? options.imageParts || {} : {};

    // Open the tab synchronously (inside the click handler) so the browser's
    // popup blocker allows it; navigate it once the PDF is ready.
    const win = openLoadingTab();

    try {
        const employee = await fetchEmployee(email);
        if (
            !employee ||
            (!employee.id && !employee.employeeId && !employee.user)
        ) {
            throw new Error('Employee record could not be found.');
        }
        const e = normalizeEmployee(employee);

        const [calendarName, logo, ...docImages] = await Promise.all([
            sections.general
                ? fetchCalendarName(e.calenderId)
                : Promise.resolve(null),
            // The wordmark SVG is tiny (162x13) — rasterize at 8x for print.
            // (Bundled asset — a plain image load is fine here.)
            loadImageDataUrl(companyLogo, { scale: 8 }),
            // Secured documents go through the authorized API fetch.
            ...collectDocumentUrls(e, parts).map((url) =>
                loadDocumentImage(url).then((img) => [url, img])
            )
        ]);
        const imageMap = Object.fromEntries(
            docImages.filter(([, img]) => img)
        );

        const doc = new jsPDF({ unit: 'pt', format: 'a4', compress: true });
        doc.setProperties({
            title: `Employee Profile - ${fullNameOf(e)}`,
            subject: 'Employee Profile',
            author: 'ECHOLOGYX eHRMS',
            creator: 'ECHOLOGYX eHRMS'
        });
        renderEmployeeProfile(doc, e, {
            detailed,
            sections,
            parts,
            imageMap,
            calendarName
        });
        finalizeDoc(doc, { logo, footerCenter: fullNameOf(e) });

        const blobUrl = doc.output('bloburl');
        if (win) win.location.href = blobUrl;
        else window.open(blobUrl, '_blank');
        onSuccess && onSuccess();
    } catch (err) {
        closeLoadingTab(win);
        const msg =
            err?.response?.data?.message ||
            err?.message ||
            'Failed to generate the employee PDF.';
        onError && onError(msg);
    }
}

export default exportEmployeePdf;
